<template>
    <div v-if="hasSupportInfo || projects.length > 0">
        <pc-loader
            v-bind:loading="submittingQuestion"
            v-bind:message="submittingMessage">
            <div
                v-if="!questionSubmitted && hasSupportInfo"
                id="questionOptionsPanel">
                <pc-input-group>
                    <pc-radio-group
                        v-model="isProjectRelated"
                        v-bind:options="questionTypes"
                        v-bind:label="isProjectRelatedText"
                        name="questionType"
                        value-prop="iprValue"
                        text-prop="label" />
                </pc-input-group>
            </div>
            <pc-transition transition="slide">
                <div
                    v-if="showSubmitButton"
                    id="projectRelatedPanel">
                    <pc-loader
                        v-bind:loading="infoLoading"
                        v-bind:message="loadingInfoMessage">
                        <pc-select
                            id="projectSelector"
                            v-model="selectedProjectIdWrapper"
                            v-bind:label="projectSelectorLabel"
                            v-bind:options="projects"
                            v-bind:validator="projectValidator"
                            text-prop="ProjectNumber"
                            value-prop="PublicId"
                            required>
                            <option
                                v-if="!Boolean(selectedProjectId)"
                                value="">
                                {{ selectPlaceholder }}
                            </option>
                        </pc-select>
                        <pc-loader
                            v-bind:loading="lookupLoading"
                            v-bind:message="loadingLookupMessage">
                            <div
                                v-if="!lookupLoading && Boolean(lookupName) && selectedProjectId"
                                id="projectLookupSection"
                                class="mt-3">
                                <span class="fw-bold">
                                    {{ lookupDataHeaderText }}
                                </span>
                                <ul
                                    v-if="lookupInfo.length > 0"
                                    style="list-style: none">
                                    <li
                                        v-for="i in lookupInfo"
                                        v-bind:key="i">
                                        {{ i }}
                                    </li>
                                </ul>
                                <p
                                    v-else
                                    class="fst-italic">
                                    {{ dataUnavailableText }}
                                </p>
                            </div>
                        </pc-loader>
                        <pc-select
                            id="categorySelector"
                            v-model="selectedCategoryId"
                            v-bind:label="categorySelectorLabel"
                            v-bind:options="categories"
                            v-bind:validator="categoryValidator"
                            text-prop="Name"
                            value-prop="Id"
                            required
                            class="mt-3">
                            <option value="">
                                {{ selectPlaceholder }}
                            </option>
                        </pc-select>
                        <pc-input
                            id="questionTitle"
                            v-model="title"
                            v-bind:label="questionTitleLabel"
                            v-bind:validator="titleValidator"
                            required
                            class="mt-3" />
                        <pc-textarea
                            id="questionDetail"
                            v-model="content"
                            v-bind:label="questionDetailLabel"
                            v-bind:validator="contentValidator"
                            v-bind:maxlength="contentCharacterLimit"
                            required
                            class="mt-3 mb-1" />
                        <div class="mb-3 text-body-secondary fst-italic">
                            {{ remainingChar }}
                        </div>
                    </pc-loader>
                    <div
                        v-if="showSubmitButton && hasVerifiedPhoneNumber"
                        id="smsNotificationOptions">
                        <pc-checkbox
                            id="enableSmsNotifications"
                            v-model="enableSmsNotifications"
                            v-bind:label="enableSmsNotificationsLabel" />
                    </div>
                </div>
            </pc-transition>
            <pc-transition transition="slide">
                <div
                    v-if="isProjectRelated === '2' && hasSupportInfo"
                    id="notProjectRelatedPanel">
                    {{ generalQuestionHeader }}<br /><br />
                    <div v-if="supportWebsite != ''">
                        {{ supportWebsiteHeader }}<br />
                        <a
                            v-bind:href="supportWebsite"
                            target="_blank">
                            {{ supportWebsite }} </a
                        ><br /><br />
                    </div>
                    <div v-if="supportEmail != ''">
                        {{ supportEmailHeader }}<br />
                        <a v-bind:href="'mailto:' + supportEmail"> {{ supportEmail }} </a><br /><br />
                    </div>
                    <div v-if="supportPhone != ''">
                        {{ supportPhoneHeader }}<br />
                        {{ supportPhone }}<br />
                    </div>
                </div>
            </pc-transition>
            <pc-transition transition="slide">
                <div
                    v-if="questionSubmitted"
                    id="questionResultPanel">
                    {{ inquirySubmittedMessageText }}
                    <br />
                    <br />
                    {{ landingPageLinkLeadin
                    }}<a v-bind:href="newThreadUrl">
                        {{ landingPageLinkTextText }}
                    </a>
                </div>
            </pc-transition>
            <div style="text-align: right">
                <pc-button-row>
                    <template v-slot:right>
                        <pc-button
                            v-if="showCancelButton"
                            v-on:click="closeQuestionDialog">
                            {{ cancelButtonText }}
                        </pc-button>
                        <pc-button
                            v-if="showSubmitButton"
                            v-bind:disabled="!submitEnabled"
                            primary
                            v-on:click="SubmitQuestion">
                            {{ submitButtonText }}
                        </pc-button>
                        <pc-button
                            v-if="showOKButton"
                            data-test-role="inquiry-ok-btn"
                            primary
                            v-on:click="closeQuestionDialog">
                            {{ okButtonText }}
                        </pc-button>
                    </template>
                </pc-button-row>
            </div>
        </pc-loader>
    </div>
    <div v-else>
        {{ noSupportInfoText }}
        <br />
        <div style="text-align: right">
            <pc-button-row>
                <template v-slot:right>
                    <pc-button
                        data-test-role="inquiry-ok-btn"
                        primary
                        v-on:click="closeQuestionDialog">
                        {{ okButtonText }}
                    </pc-button>
                </template>
            </pc-button-row>
        </div>
    </div>
</template>

<script>
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/addInquiry.i18n";

    const iprVals = Object.freeze({ none: "0", related: "1", unrelated: "2" });
    const CONTENT_CHARACTER_LIMIT = 4000;

    export default {
        inject: { ProjectId: { default: null } },
        data() {
            let currProjectId = "";
            if (this.ProjectId) {
                currProjectId = this.ProjectId;
            }
            return {
                questionSubmitted: false,
                isProjectRelated: iprVals.none,
                infoLoading: true,
                projects: [],
                selectedProjectId: currProjectId,
                lookupLoading: false,
                lookupName: null,
                lookupInfo: [],
                categories: [],
                selectedCategoryId: "",
                title: null,
                content: null,
                contentCharacterLimit: CONTENT_CHARACTER_LIMIT,
                controller: this.$serviceControllers.ProjectInquiriesController,
                programId: this.$serviceControllers.ProjectInquiriesController.ProgramId,
                newThreadName: "",
                newThreadUrl: "",
                submittingQuestion: false,
                enableSmsNotificationsInternal: false,
                hasVerifiedPhoneNumber: false
            };
        },
        computed: {
            enableSmsNotifications: {
                get() {
                    return this.enableSmsNotificationsInternal;
                },
                set(value) {
                    this.enableSmsNotificationsInternal = value;
                }
            },
            showCancelButton() {
                return (
                    (this.isProjectRelated == iprVals.none && this.hasSupportInfo) ||
                    ((this.isProjectRelated == iprVals.related || !this.hasSupportInfo) && !this.questionSubmitted)
                );
            },
            showSubmitButton() {
                return (this.isProjectRelated == iprVals.related || !this.hasSupportInfo) && !this.questionSubmitted;
            },
            showOKButton() {
                return (
                    this.isProjectRelated == iprVals.unrelated ||
                    ((this.isProjectRelated == iprVals.related || !this.hasSupportInfo) && this.questionSubmitted)
                );
            },
            selectedProjectIdWrapper: {
                get() {
                    return this.selectedProjectId;
                },
                set(value) {
                    this.selectedProjectId = value;
                    this.GetLookupInfo();
                }
            },
            projectValidator() {
                return getValidator(this.$app, {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.invalidProjectMessage
                });
            },
            categoryValidator() {
                return getValidator(this.$app, {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.invalidCategoryMessage
                });
            },
            titleValidator() {
                return getValidator(this.$app, {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.invalidTitleMessage
                });
            },
            contentValidator() {
                return getValidator(this.$app, {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.invalidContentMessage
                });
            },
            submitEnabled() {
                return this.selectedProjectId && this.selectedCategoryId && this.title && this.content;
            },
            supportWebsite() {
                let website = this.controller.SupportInfo.Website || "";
                if (website && !website.startsWith("http://") && !website.startsWith("https://")) {
                    website = "http://" + website;
                }
                return website;
            },
            supportEmail() {
                return this.controller.SupportInfo.Email ?? "";
            },
            supportPhone() {
                return this.controller.SupportInfo.Phone ?? "";
            },
            useLookup() {
                return this.controller.SupportInfo.UseLookup;
            },
            selectedProjectNumber() {
                const project = this.GetSelectedProject();
                if (project) {
                    return project.ProjectNumber;
                } else return "";
            },
            questionTypes() {
                return [
                    {
                        label: this.relatedOptionText,
                        iprValue: iprVals.related
                    },
                    {
                        label: this.notRelatedOptionText,
                        iprValue: iprVals.unrelated
                    }
                ];
            },
            //Calculates number of characters available in the inquiry detail
            contentRemainingChars() {
                if (!this.content) {
                    return CONTENT_CHARACTER_LIMIT;
                }
                const remaining = CONTENT_CHARACTER_LIMIT - this.content.length;
                return remaining > 0 ? remaining : 0;
            },
            hasSupportInfo() {
                return (
                    this.supportWebsite.trim() !== "" ||
                    this.supportEmail.trim() !== "" ||
                    this.supportPhone.trim() !== ""
                );
            },
            ...mapI18n(
                {
                    ...KEYS,
                    remainingChar() {
                        return {
                            key: KEYS.remainingCharMsg,
                            values: { charactersRemaining: this.contentRemainingChars }
                        };
                    },
                    lookupDataHeaderText() {
                        return {
                            key: KEYS.lookupDataHeader,
                            values: {
                                lookupField: this.lookupName,
                                projectNumber: this.selectedProjectNumber
                            }
                        };
                    },
                    inquirySubmittedMessageText() {
                        return {
                            key: KEYS.inquirySubmittedMessage,
                            values: { inquiryTitle: this.newThreadName }
                        };
                    },
                    landingPageLinkTextText() {
                        return {
                            key: KEYS.landingPageLinkText,
                            values: { projectNumber: this.selectedProjectNumber }
                        };
                    }
                },
                NAMESPACE
            )
        },
        created() {
            this.GetInfo();
        },
        async mounted() {
            this.hasVerifiedPhoneNumber = await this.getUserHasVerifiedPhoneNumber();
        },
        methods: {
            closeQuestionDialog() {
                this.$app.closeQuestionDialog();
                this.clearData();
            },
            clearData() {
                this.isProjectRelated = iprVals.none;
                this.questionSubmitted = false;
                this.newThreadName = "";
                this.newThreadUrl = "";
                this.title = null;
                this.content = null;
            },
            GetSelectedProject() {
                const spi = this.selectedProjectId;
                if (spi) {
                    return this.projects.find(function (p) {
                        return p.PublicId === spi;
                    });
                } else return null;
            },
            async GetInfo() {
                const result = await this.controller.GetInquiryInfo({
                    programId: this.programId
                });
                this.projects = result.Data.Projects.reverse();
                if (this.selectedProjectId && !this.projects.some((p) => p.PublicId === this.selectedProjectId)) {
                    // If we are in a project context, but the project is unsubmitted
                    this.selectedProjectId = "";
                }
                this.GetLookupInfo();
                this.categories = result.Data.Categories;
                this.infoLoading = false;
            },
            async GetLookupInfo() {
                if (this.useLookup && this.selectedProjectId) {
                    this.lookupLoading = true;
                    const result = await this.controller.GetProjectLookupInfo({
                        programId: this.programId,
                        projectId: this.selectedProjectId
                    });
                    if (result.Data != null) {
                        this.lookupName = result.Data.LookupName;
                        this.lookupInfo = result.Data.LookupInfo;
                    }
                    this.lookupLoading = false;
                }
            },
            async SubmitQuestion() {
                if (this.submittingQuestion) {
                    return;
                }

                this.submittingQuestion = true;
                try {
                    const result = await this.controller.SaveNewThread({
                        programId: this.programId,
                        projectId: this.selectedProjectId,
                        categoryId: this.selectedCategoryId,
                        title: this.title,
                        content: this.content,
                        enableSmsNotifications: this.enableSmsNotifications
                    });
                    this.newThreadName = result.Data.NewThread.FriendlyName;
                    this.newThreadUrl = result.Data.AdminPageUrl;
                    this.questionSubmitted = true;
                } finally {
                    this.submittingQuestion = false;
                }
            },
            async getUserHasVerifiedPhoneNumber() {
                const result =
                    await this.$serviceControllers.ProjectInquiriesController.GetUserHasVerifiedPhoneNumber();
                return result.Data.HasVerifiedPhoneNumber;
            }
        }
    };
</script>
